import { useState, type ChangeEvent } from 'react';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../../lib/firebase';
import { useAuthStore } from '../../store/authStore';
import { useUiStore } from '../../store/uiStore';
import { Spinner } from '../../components/Spinner';

export function Profile() {
  const profile = useAuthStore((s) => s.profile);
  const firebaseUser = useAuthStore((s) => s.firebaseUser);
  const pushToast = useUiStore((s) => s.pushToast);

  const [uploading, setUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(
    profile?.avatarUrl ?? null,
  );

  async function handleAvatarChange(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const uid = profile?.uid ?? firebaseUser?.uid;
    if (!uid) {
      pushToast('error', 'You must be signed in to upload an avatar.');
      return;
    }
    setUploading(true);
    try {
      const storageRef = ref(storage, `avatars/${uid}/${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setAvatarUrl(url);
      // NOTE: Persisting this URL to the user's profile document (via a
      // Netlify Function) is a future enhancement — for now we only display it.
      pushToast('success', `Avatar uploaded: ${url}`);
    } catch (err) {
      pushToast(
        'error',
        err instanceof Error ? err.message : 'Avatar upload failed',
      );
    } finally {
      setUploading(false);
    }
  }

  if (!profile) {
    return (
      <div className="flex justify-center py-10">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-100">Profile</h1>

      <section className="card space-y-4">
        <div className="flex items-center gap-4">
          <div className="h-20 w-20 overflow-hidden rounded-full border border-white/15 bg-felt-dark">
            {avatarUrl ? (
              <img
                src={avatarUrl}
                alt="Avatar"
                className="h-full w-full object-cover"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-3xl text-slate-500">
                {profile.displayName.charAt(0).toUpperCase() || '?'}
              </div>
            )}
          </div>
          <div>
            <p className="text-lg font-semibold text-slate-100">
              {profile.displayName}
            </p>
            <p className="text-sm text-slate-400">{profile.email ?? '—'}</p>
          </div>
        </div>

        <div>
          <label className="label" htmlFor="avatar-upload">
            Upload avatar
          </label>
          <input
            id="avatar-upload"
            type="file"
            accept="image/*"
            onChange={handleAvatarChange}
            disabled={uploading}
            className="text-sm text-slate-300 file:mr-3 file:rounded-lg file:border-0 file:bg-gold file:px-3 file:py-1 file:font-semibold file:text-felt-dark hover:file:bg-gold-dark"
          />
          {uploading && (
            <div className="mt-2">
              <Spinner />
            </div>
          )}
        </div>
      </section>

      <section className="card space-y-3">
        <h2 className="text-lg font-semibold text-slate-100">Details</h2>
        <dl className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <dt className="label">Display name</dt>
            <dd className="text-slate-100">{profile.displayName}</dd>
          </div>
          <div>
            <dt className="label">Email</dt>
            <dd className="text-slate-100">{profile.email ?? '—'}</dd>
          </div>
          <div>
            <dt className="label">Role</dt>
            <dd className="capitalize text-slate-100">{profile.role}</dd>
          </div>
          <div>
            <dt className="label">Member since</dt>
            <dd className="text-slate-100">
              {new Date(profile.createdAt).toLocaleDateString()}
            </dd>
          </div>
          <div>
            <dt className="label">Email verified</dt>
            <dd
              className={
                firebaseUser?.emailVerified
                  ? 'text-emerald-400'
                  : 'text-rose-400'
              }
            >
              {firebaseUser?.emailVerified ? 'Verified' : 'Not verified'}
            </dd>
          </div>
        </dl>
      </section>
    </div>
  );
}
